import { useState, useCallback, useEffect, useMemo } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn, formatDate } from "@/lib/utils";
import { formatSearchDay, formatSearchRange } from "@/lib/search-date-label";
import { format, parseISO } from "date-fns";
import { getDateFnsLocale, getSundayFirstWeekdays } from "@/i18n/dateLocale";
import { calculateChargeableDays } from "@/lib/days";
import type { AvailabilityData } from "@/lib/api";

export interface DateRangePopupProps {
  selectedFrom: string;
  selectedTo: string;
  onChange: (from: string, to: string) => void;
  onClose: () => void;

  /**
   * Optional availability data. When supplied, the calendar disables
   * blocked dates and enforces the host's advance notice / minimum
   * booking length. Homepage and search-results surfaces have no car
   * selected at this point, so they pass nothing — only past days are
   * disabled.
   */
  availability?: AvailabilityData | null;
  rentalType?: "driving" | "chauffeured" | "event";
  initialActiveField?: "start" | "end";

  /** Number of side-by-side months. Defaults to 2. */
  monthsToShow?: 1 | 2;
  /** Empty-state header title. */
  emptyTitle?: string;
  /** Empty-state header subtitle. */
  emptySubtitle?: string;
  /** Footer "Done"/close button label. */
  doneLabel?: string;
  /** Footer "Clear dates" button label. */
  clearLabel?: string;
  /** Tailwind width class for the popup root. Defaults to the
   * two-month checkout width. */
  widthClassName?: string;
  /** Only search entry uses neutral endpoint labels; booking keeps check-in/out. */
  searchLabels?: boolean;
}

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}
function getFirstDayOfWeek(year: number, month: number) {
  return new Date(year, month, 1).getDay();
}
function toDayStr(s: string): string {
  return s.length >= 10 ? s.slice(0, 10) : s;
}
function isDateBlocked(date: Date, blockedDates: AvailabilityData["blockedDates"]) {
  const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  return blockedDates.some(bd => dateStr >= toDayStr(bd.start_date) && dateStr <= toDayStr(bd.end_date));
}
function hasBlockedDateInRange(from: string, to: string, blockedDates: AvailabilityData["blockedDates"]): boolean {
  return blockedDates.some(bd => toDayStr(bd.start_date) <= to && toDayStr(bd.end_date) >= from);
}
function formatMonth(year: number, month: number) {
  return formatDate(`${year}-${String(month + 1).padStart(2, "0")}-01`, { month: "long", year: "numeric" });
}
function formatHeaderRange(from: string, to: string): string {
  const fromFmt = formatDate(from, { month: "short", day: "numeric", year: "numeric" });
  const toFmt = formatDate(to, { month: "short", day: "numeric", year: "numeric" });
  return `${fromFmt} – ${toFmt}`;
}
function formatPillDate(d: string): string {
  return formatDate(d, { month: "numeric", day: "numeric", year: "2-digit" });
}

export function DateRangePopup({
  availability,
  selectedFrom,
  selectedTo,
  rentalType,
  initialActiveField = "start",
  onChange,
  onClose,
  monthsToShow = 2,
  emptyTitle,
  emptySubtitle,
  doneLabel,
  clearLabel,
  widthClassName,
  searchLabels = false,
}: DateRangePopupProps) {
  const { t, i18n } = useTranslation();
  const today = new Date();
  const weekdays = useMemo(() => getSundayFirstWeekdays(i18n.language), [i18n.language]);

  const resolvedEmptyTitle = emptyTitle ?? t("dateRange.select_dates");
  const resolvedEmptySubtitle = emptySubtitle ?? t("dateRange.add_travel_dates");
  const resolvedDoneLabel = doneLabel ?? t("common.close");
  const resolvedClearLabel = clearLabel ?? t("dateRange.clear_dates");

  const minBookingError = (selectedDays: number) =>
    `${t("dateRange.min_booking_is", { count: minDays })} ${t("dateRange.you_selected", { count: selectedDays })}`;

  const [activeField, setActiveField] = useState<"start" | "end">(initialActiveField);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    setActiveField(initialActiveField);
  }, [initialActiveField]);

  const [baseMonth, setBaseMonth] = useState(() => {
    if (selectedFrom) {
      const seed = new Date(`${selectedFrom}T00:00:00`);
      if (!Number.isNaN(seed.getTime())) {
        return { year: seed.getFullYear(), month: seed.getMonth() };
      }
    }
    return { year: today.getFullYear(), month: today.getMonth() };
  });

  const blockedDates = availability?.blockedDates ?? [];
  const advanceNotice = availability?.window?.advance_notice_days || 0;
  const minDays = (rentalType === "chauffeured" || rentalType === "event")
    ? 1
    : (availability?.window?.min_booking_days || 1);
  const earliestDate = new Date(today);
  earliestDate.setHours(0, 0, 0, 0);
  earliestDate.setDate(earliestDate.getDate() + advanceNotice);

  const months = useMemo(() => {
    const m1 = { year: baseMonth.year, month: baseMonth.month };
    if (monthsToShow === 1) return [m1];
    let m2month = baseMonth.month + 1;
    let m2year = baseMonth.year;
    if (m2month > 11) { m2month = 0; m2year++; }
    return [m1, { year: m2year, month: m2month }];
  }, [baseMonth, monthsToShow]);

  const handlePrev = () => {
    let m = baseMonth.month - 1;
    let y = baseMonth.year;
    if (m < 0) { m = 11; y--; }
    if (y > today.getFullYear() - 1) setBaseMonth({ year: y, month: m });
  };

  const handleNext = () => {
    let m = baseMonth.month + 1;
    let y = baseMonth.year;
    if (m > 11) { m = 0; y++; }
    setBaseMonth({ year: y, month: m });
  };

  const nights = (selectedFrom && selectedTo) ? calculateChargeableDays(selectedFrom, selectedTo) : 0;

  const handleDayClick = useCallback((dateStr: string) => {
    setValidationError(null);

    // Chauffeured is a single-day, multi-hour service: the drop-off date is
    // always locked to the pick-up date, so any day click selects that one
    // day. A cross-midnight chauffeured booking can't be represented by the
    // per-day-hours pricing model, so a range is never offered here.
    if (rentalType === "chauffeured") {
      onChange(dateStr, dateStr);
      return;
    }

    const startNewRange = () => {
      onChange(dateStr, "");
      setActiveField("end");
    };

    if (activeField === "start") {
      startNewRange();
      return;
    }

    if (!selectedFrom) {
      startNewRange();
      return;
    }

    if (dateStr < selectedFrom) {
      startNewRange();
      return;
    }

    if (dateStr === selectedFrom) {
      if (1 < minDays) {
        setValidationError(minBookingError(1));
        return;
      }
      onChange(selectedFrom, dateStr);
      return;
    }

    const duration = calculateChargeableDays(selectedFrom, dateStr);
    if (duration < minDays) {
      setValidationError(minBookingError(duration));
      return;
    }

    if (hasBlockedDateInRange(selectedFrom, dateStr, blockedDates)) {
      setValidationError(t("dateRange.range_unavailable"));
      return;
    }

    onChange(selectedFrom, dateStr);
  }, [activeField, selectedFrom, minDays, blockedDates, onChange, rentalType]);

  const handleClearAll = useCallback(() => {
    setValidationError(null);
    onChange("", "");
    setActiveField("start");
  }, [onChange]);

  const handleClearStart = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setValidationError(null);
    onChange("", "");
    setActiveField("start");
  };

  const handleClearEnd = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setValidationError(null);
    if (selectedFrom) {
      onChange(selectedFrom, "");
    }
    setActiveField("end");
  };

  const renderMonth = (year: number, month: number) => {
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfWeek(year, month);
    const cells: React.ReactElement[] = [];

    for (let i = 0; i < firstDay; i++) {
      cells.push(<div key={`empty-${i}`} className="h-10 w-10" />);
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      const isPast = date < earliestDate;
      const blocked = isDateBlocked(date, blockedDates);
      const disabled = isPast || blocked;
      const isStart = dateStr === selectedFrom;
      const isEnd = !!(selectedTo && dateStr === selectedTo && selectedTo !== selectedFrom);
      const isSameDay = !!(selectedFrom && selectedTo && selectedFrom === selectedTo && dateStr === selectedFrom);
      const isInRange = !!(selectedFrom && selectedTo && selectedFrom !== selectedTo && dateStr > selectedFrom && dateStr < selectedTo);
      const isToday = date.toDateString() === today.toDateString();

      cells.push(
        <button
          key={d}
          type="button"
          disabled={disabled}
          onClick={() => !disabled && handleDayClick(dateStr)}
          aria-label={dateStr}
          data-testid={`popup-day-${dateStr}`}
          className={cn(
            "relative flex h-10 w-10 items-center justify-center text-sm transition-colors",
            disabled && "cursor-not-allowed text-muted-foreground/40 line-through",
            blocked && !isPast && "bg-destructive/5",
            !disabled && !isStart && !isEnd && !isSameDay && !isInRange && "rounded-full hover:bg-muted",
            isSameDay && "rounded-full bg-foreground text-background font-semibold",
            isStart && !isSameDay && "rounded-l-full bg-foreground text-background font-semibold",
            isEnd && !isSameDay && "rounded-r-full bg-foreground text-background font-semibold",
            isInRange && "bg-foreground/10",
            isToday && !isStart && !isEnd && !isSameDay && "font-semibold text-primary",
          )}
        >
          {d}
        </button>
      );
    }

    return (
      <div>
        <h4 className="mb-3 text-center text-base font-semibold text-foreground">
          {searchLabels
            ? format(parseISO(`${year}-${String(month + 1).padStart(2, "0")}-01`), "MMMM yyyy", { locale: getDateFnsLocale(i18n.language) })
            : formatMonth(year, month)}
        </h4>
        <div className="grid grid-cols-7 gap-y-1 justify-items-center">
          {weekdays.map(({ label, name }, i) => (
            <div key={i} className="flex h-8 w-10 items-center justify-center text-xs font-medium text-muted-foreground">
              <span aria-hidden="true">{label}</span>
              <span className="sr-only">{name}</span>
            </div>
          ))}
          {cells}
        </div>
      </div>
    );
  };

  const headerLabel = searchLabels
    ? formatSearchRange(selectedFrom, selectedTo, i18n.language)
    : (() => {
    if (selectedFrom && selectedTo && selectedFrom !== selectedTo) {
      return formatHeaderRange(selectedFrom, selectedTo);
    }
    if (selectedFrom) {
      return formatDate(selectedFrom, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
    }
    return null;
  })();

  const rootWidth = widthClassName ?? (monthsToShow === 2 ? "w-[760px] max-w-[96vw]" : "w-[380px] max-w-[96vw]");
  const monthsGridCols = monthsToShow === 2 ? "grid-cols-2 gap-8" : "grid-cols-1";

  return (
    <div
      className={cn("flex flex-col", rootWidth)}
      style={{ maxHeight: "var(--radix-popover-content-available-height)" }}
    >
      <div className="flex shrink-0 items-start justify-between gap-4 border-b border-border px-6 pt-6 pb-4">
        <div>
          {nights > 0 ? (
            <>
              <div className="text-lg font-semibold text-foreground" data-testid="popup-nights">
                {t(searchLabels ? "dateRange.days_selected" : "dateRange.nights", { count: nights })}
              </div>
              {headerLabel && <div className="mt-0.5 text-sm text-muted-foreground">{headerLabel}</div>}
            </>
          ) : (
            <>
              <div className="text-lg font-semibold text-foreground">{resolvedEmptyTitle}</div>
              <div className="mt-0.5 text-sm text-muted-foreground">{resolvedEmptySubtitle}</div>
            </>
          )}
        </div>
        <div className="flex items-stretch gap-2">
          <button
            type="button"
            onClick={() => { setActiveField("start"); setValidationError(null); }}
            data-testid="popup-pill-checkin"
            className={cn(
              "flex flex-col items-start rounded-lg border px-3 py-1.5 text-left transition-colors",
              activeField === "start"
                ? "border-foreground bg-background ring-1 ring-foreground"
                : "border-border bg-background hover:border-muted-foreground/40"
            )}
          >
            <span className="text-[10px] font-bold uppercase tracking-wider text-foreground">{t(searchLabels ? "dateRange.search_from" : "dateRange.check_in")}</span>
            <span className="mt-0.5 flex items-center gap-2">
              <span className={cn("text-sm", selectedFrom ? "text-foreground" : "text-muted-foreground")}>
                {selectedFrom ? (searchLabels ? formatSearchDay(selectedFrom, i18n.language) : formatPillDate(selectedFrom)) : t("dateRange.add_date")}
              </span>
              {selectedFrom && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={handleClearStart}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleClearStart(e); }}
                  className="rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                  aria-label={t(searchLabels ? "dateRange.clear_search_from" : "dateRange.clear_check_in")}
                  data-testid="popup-pill-checkin-clear"
                >
                  <X className="h-3 w-3" />
                </span>
              )}
            </span>
          </button>
          <button
            type="button"
            onClick={() => { setActiveField("end"); setValidationError(null); }}
            data-testid="popup-pill-checkout"
            className={cn(
              "flex flex-col items-start rounded-lg border px-3 py-1.5 text-left transition-colors",
              activeField === "end"
                ? "border-foreground bg-background ring-1 ring-foreground"
                : "border-border bg-background hover:border-muted-foreground/40"
            )}
          >
            <span className="text-[10px] font-bold uppercase tracking-wider text-foreground">{t(searchLabels ? "dateRange.search_to" : "dateRange.checkout")}</span>
            <span className="mt-0.5 flex items-center gap-2">
              <span className={cn("text-sm", selectedTo ? "text-foreground" : "text-muted-foreground")}>
                {selectedTo ? (searchLabels ? formatSearchDay(selectedTo, i18n.language) : formatPillDate(selectedTo)) : t("dateRange.add_date")}
              </span>
              {selectedTo && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={handleClearEnd}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleClearEnd(e); }}
                  className="rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                  aria-label={t(searchLabels ? "dateRange.clear_search_to" : "dateRange.clear_checkout")}
                  data-testid="popup-pill-checkout-clear"
                >
                  <X className="h-3 w-3" />
                </span>
              )}
            </span>
          </button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
        <div className="relative">
          <button
            onClick={handlePrev}
            className="absolute left-0 top-0 z-10 inline-flex h-11 w-11 cursor-pointer items-center justify-center rounded-full text-foreground hover:bg-muted"
            aria-label={t("dateRange.previous_month")}
            data-testid="popup-prev-month"
            type="button"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            onClick={handleNext}
            className="absolute right-0 top-0 z-10 inline-flex h-11 w-11 cursor-pointer items-center justify-center rounded-full text-foreground hover:bg-muted"
            aria-label={t("dateRange.next_month")}
            data-testid="popup-next-month"
            type="button"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
          <div className={cn("grid", monthsGridCols)}>
            {months.map(m => (
              <div key={`${m.year}-${m.month}`}>{renderMonth(m.year, m.month)}</div>
            ))}
          </div>
        </div>
        {(minDays > 1 || advanceNotice > 0) && (
          <p className="mt-3 text-center text-xs text-muted-foreground">
            {minDays > 1 && <>{t("dateRange.minimum_days", { count: minDays })}</>}
            {minDays > 1 && advanceNotice > 0 && <> · </>}
            {advanceNotice > 0 && <>{t("dateRange.advance_notice", { count: advanceNotice })}</>}
          </p>
        )}
        {validationError && (
          <div className="mt-3 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {validationError}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between border-t border-border px-6 py-4">
        <button
          type="button"
          onClick={handleClearAll}
          data-testid="popup-clear-dates"
          disabled={!selectedFrom && !selectedTo}
          className="rounded-md text-sm font-semibold text-foreground underline underline-offset-4 hover:text-foreground/80 disabled:cursor-not-allowed disabled:text-muted-foreground disabled:no-underline"
        >
          {resolvedClearLabel}
        </button>
        <button
          type="button"
          onClick={onClose}
          data-testid="popup-close"
          className="rounded-lg bg-foreground px-5 py-2 text-sm font-semibold text-background transition-opacity hover:opacity-90"
        >
          {resolvedDoneLabel}
        </button>
      </div>
    </div>
  );
}

export default DateRangePopup;
